import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CollectionsIcon from '@mui/icons-material/Collections';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import HubIcon from '@mui/icons-material/Hub';
import ImageIcon from '@mui/icons-material/Image';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PaletteIcon from '@mui/icons-material/Palette';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import StoreIcon from '@mui/icons-material/Store';
import SyncIcon from '@mui/icons-material/Sync';
import TimelineIcon from '@mui/icons-material/Timeline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useAuth } from '../auth/AuthContext';
import { useUploadManager } from '../upload/UploadContext';
import JobsDock from '../components/JobsDock';
import { useFeatureFlags } from '../features/FeatureFlagsContext';

const drawerWidth = 264;
const jobsDockOffset = 56;
const menuIconSx = { minWidth: 34, color: 'inherit' };
const nestedMenuSx = { pl: 4.5 };
const platformIconSx = {
  width: 18,
  height: 18,
  objectFit: 'contain',
  display: 'block',
};

function isPathSelected(pathname, prefixes) {
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function NavSection({ label, children }) {
  return (
    <Box component="section" sx={{ mt: 1.75 }}>
      <Typography
        component="h2"
        variant="overline"
        color="text.secondary"
        sx={{ display: 'block', px: 2.5, pb: 0.5 }}
      >
        {label}
      </Typography>
      <List disablePadding>{children}</List>
    </Box>
  );
}

function NavItem({ to, selected, icon, label, onNavigate, sx }) {
  return (
    <ListItemButton
      component={NavLink}
      to={to}
      selected={selected}
      onClick={onNavigate}
      sx={{ mx: 1.25, minHeight: 42, borderRadius: 2, ...sx }}
    >
      <ListItemIcon sx={menuIconSx}>{icon}</ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2', fontWeight: selected ? 700 : 550 }} />
    </ListItemButton>
  );
}

function NavToggle({ selected, open, onClick, controls, icon, label, sx }) {
  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      aria-expanded={open}
      aria-controls={controls}
      sx={{ mx: 1.25, minHeight: 42, borderRadius: 2, ...sx }}
    >
      <ListItemIcon sx={menuIconSx}>{icon}</ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2', fontWeight: selected ? 700 : 550 }} />
      {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
    </ListItemButton>
  );
}

function PlatformIcon({ src }) {
  return <Box component="img" src={src} alt="" sx={platformIconSx} />;
}

const SidebarLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const { logout, username } = useAuth();
  const { flags } = useFeatureFlags();
  const { hasActiveUploads, overallProgress } = useUploadManager();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [createAnchor, setCreateAnchor] = React.useState(null);
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [dynamicOpen, setDynamicOpen] = React.useState(false);
  const [printifyOpen, setPrintifyOpen] = React.useState(false);
  const [etsyOpen, setEtsyOpen] = React.useState(false);
  const [woocommerceOpen, setWooCommerceOpen] = React.useState(false);

  const selected = React.useMemo(() => ({
    home: isPathSelected(location.pathname, ['/home']),
    launch: isPathSelected(location.pathname, ['/launch']),
    catalog: isPathSelected(location.pathname, ['/catalog']),
    designs: isPathSelected(location.pathname, ['/designs']),
    assets: isPathSelected(location.pathname, ['/assets']),
    products: isPathSelected(location.pathname, ['/products']),
    listings: isPathSelected(location.pathname, ['/listings']),
    collections: isPathSelected(location.pathname, ['/collections']),
    connections: isPathSelected(location.pathname, ['/connections']),
    activity: isPathSelected(location.pathname, ['/activity']),
    upload: isPathSelected(location.pathname, ['/upload']),
    images: isPathSelected(location.pathname, ['/gallery']),
    dynamicAccounts: isPathSelected(location.pathname, ['/mockups/dynamic/accounts', '/dynamic/accounts']),
    dynamicCatalogs: isPathSelected(location.pathname, ['/mockups/dynamic/catalogs', '/dynamic/catalogs']),
    dynamicMockups: isPathSelected(location.pathname, ['/mockups/dynamic/mockups', '/dynamic/mockups']),
    printifyStores: isPathSelected(location.pathname, ['/fulfillment/printify/stores']),
    printifyCatalog: isPathSelected(location.pathname, ['/fulfillment/printify/catalog']),
    printifyProducts: isPathSelected(location.pathname, ['/fulfillment/printify/products']),
    printifyOrders: isPathSelected(location.pathname, ['/fulfillment/printify/orders']),
    spoonflowerProducts: isPathSelected(location.pathname, ['/sales/spoonflower/products']),
    etsyShops: isPathSelected(location.pathname, ['/sales/etsy/shops', '/shops']),
    etsySections: isPathSelected(location.pathname, ['/sales/etsy/sections', '/sections']),
    etsyListings: isPathSelected(location.pathname, ['/sales/etsy/listings']),
    wooSites: isPathSelected(location.pathname, ['/sales/woocommerce/sites']),
    wooCategories: isPathSelected(location.pathname, ['/sales/woocommerce/categories']),
    wooListings: isPathSelected(location.pathname, ['/sales/woocommerce/listings']),
    wooMockups: isPathSelected(location.pathname, ['/sales/woocommerce/mockups']),
    operations: isPathSelected(location.pathname, ['/system/operations']),
  }), [location.pathname]);

  const dynamicSelected = selected.dynamicAccounts || selected.dynamicCatalogs || selected.dynamicMockups;
  const printifySelected = selected.printifyStores || selected.printifyCatalog || selected.printifyProducts || selected.printifyOrders;
  const etsySelected = selected.etsyShops || selected.etsySections || selected.etsyListings;
  const wooSelected = selected.wooSites || selected.wooCategories || selected.wooListings || selected.wooMockups;
  const advancedSelected = selected.assets || selected.upload || selected.images || dynamicSelected
    || printifySelected || selected.spoonflowerProducts || etsySelected || wooSelected || selected.operations;

  React.useEffect(() => {
    if (advancedSelected) setAdvancedOpen(true);
    if (dynamicSelected) setDynamicOpen(true);
    if (printifySelected) setPrintifyOpen(true);
    if (etsySelected) setEtsyOpen(true);
    if (wooSelected) setWooCommerceOpen(true);
  }, [advancedSelected, dynamicSelected, etsySelected, printifySelected, wooSelected]);

  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const closeMobile = () => setMobileOpen(false);
  const chooseCreate = (path) => {
    setCreateAnchor(null);
    closeMobile();
    navigate(path);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: `${jobsDockOffset}px` }}>
      <Box
        component={NavLink}
        to={flags.unified_reads ? '/home' : '/upload'}
        onClick={closeMobile}
        aria-label="PODBoss home"
        sx={{ display: 'block', px: 2.5, pt: 2.25, pb: 1.25 }}
      >
        <Box
          component="img"
          src="/assets/logo.webp"
          alt="POD Boss"
          sx={{ width: '100%', maxWidth: 164, height: 'auto', display: 'block' }}
        />
      </Box>

      {flags.unified_reads && flags.unified_writes ? (
        <Box sx={{ px: 1.5, pb: 0.5 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            aria-haspopup="menu"
            aria-expanded={Boolean(createAnchor)}
            onClick={(event) => setCreateAnchor(event.currentTarget)}
          >
            Create
          </Button>
          <Menu anchorEl={createAnchor} open={Boolean(createAnchor)} onClose={() => setCreateAnchor(null)}>
            <MenuItem onClick={() => chooseCreate('/launch')}><RocketLaunchIcon fontSize="small" sx={{ mr: 1.5 }} />Launch a product</MenuItem>
            <Divider />
            <MenuItem onClick={() => chooseCreate('/designs/new')}><PaletteIcon fontSize="small" sx={{ mr: 1.5 }} />New design</MenuItem>
            <MenuItem onClick={() => chooseCreate('/products/new')}><Inventory2Icon fontSize="small" sx={{ mr: 1.5 }} />New product</MenuItem>
            <MenuItem onClick={() => chooseCreate('/listings/new')}><ListAltIcon fontSize="small" sx={{ mr: 1.5 }} />New listing</MenuItem>
          </Menu>
        </Box>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: 2 }}>
        {flags.unified_reads ? (
          <>
            <NavItem to="/home" selected={selected.home} icon={<HomeRoundedIcon />} label="Home" onNavigate={closeMobile} />

            <NavSection label="Build">
              <NavItem to="/catalog" selected={selected.catalog} icon={<CategoryIcon />} label="Catalog" onNavigate={closeMobile} />
              <NavItem to="/designs" selected={selected.designs} icon={<PaletteIcon />} label="Designs" onNavigate={closeMobile} />
              <NavItem to="/products" selected={selected.products} icon={<Inventory2Icon />} label="Products" onNavigate={closeMobile} />
            </NavSection>

            <NavSection label="Sell">
              <NavItem to="/listings" selected={selected.listings} icon={<ListAltIcon />} label="Listings" onNavigate={closeMobile} />
              <NavItem to="/collections" selected={selected.collections} icon={<AccountTreeIcon />} label="Collections" onNavigate={closeMobile} />
            </NavSection>

            <NavSection label="Operate">
              <NavItem to="/activity" selected={selected.activity} icon={<TimelineIcon />} label="Activity" onNavigate={closeMobile} />
            </NavSection>

            <NavSection label="Settings">
              <NavItem to="/connections" selected={selected.connections} icon={<HubIcon />} label="Connections" onNavigate={closeMobile} />
            </NavSection>
          </>
        ) : null}

        <NavSection label="Advanced">
          <NavToggle
            selected={advancedSelected}
            open={advancedOpen}
            onClick={() => setAdvancedOpen((current) => !current)}
            controls="advanced-navigation"
            icon={<MoreHorizIcon />}
            label="Provider tools"
          />
          <Collapse id="advanced-navigation" in={advancedOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {flags.unified_reads ? (
                <NavItem to="/assets" selected={selected.assets} icon={<CollectionsIcon fontSize="small" />} label="Asset library" onNavigate={closeMobile} sx={nestedMenuSx} />
              ) : null}
              <NavItem to="/upload" selected={selected.upload} icon={<CloudUploadIcon fontSize="small" />} label="Upload" onNavigate={closeMobile} sx={nestedMenuSx} />
              {hasActiveUploads ? (
                <Box sx={{ px: 3.5, py: 0.5 }}>
                  <LinearProgress variant="determinate" value={overallProgress ?? 0} aria-label="Upload progress" />
                </Box>
              ) : null}
              <NavItem to="/gallery" selected={selected.images} icon={<ImageIcon fontSize="small" />} label="Images" onNavigate={closeMobile} sx={nestedMenuSx} />
              <NavItem
                to="/sales/spoonflower/products"
                selected={selected.spoonflowerProducts}
                icon={<StoreIcon fontSize="small" />}
                label="Spoonflower products"
                onNavigate={closeMobile}
                sx={nestedMenuSx}
              />

              <NavToggle
                selected={dynamicSelected}
                open={dynamicOpen}
                onClick={() => setDynamicOpen((current) => !current)}
                controls="dynamic-navigation"
                icon={<PlatformIcon src="/assets/favicons/dynamic.png" />}
                label="Mockup providers"
                sx={nestedMenuSx}
              />
              <Collapse id="dynamic-navigation" in={dynamicOpen} timeout="auto" unmountOnExit>
                <List disablePadding>
                  <NavItem to="/mockups/dynamic/accounts" selected={selected.dynamicAccounts} icon={<VpnKeyIcon fontSize="small" />} label="Accounts" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                  <NavItem to="/mockups/dynamic/catalogs" selected={selected.dynamicCatalogs} icon={<CategoryIcon fontSize="small" />} label="Catalogs" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                  <NavItem to="/mockups/dynamic/mockups" selected={selected.dynamicMockups} icon={<DashboardCustomizeIcon fontSize="small" />} label="Mockups" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                </List>
              </Collapse>

              {flags.legacy_navigation ? (
                <>
                  <Divider sx={{ my: 1, mx: 2 }} />
                  <NavToggle selected={printifySelected} open={printifyOpen} onClick={() => setPrintifyOpen((current) => !current)} controls="printify-navigation" icon={<PlatformIcon src="/assets/favicons/printify.png" />} label="Printify" sx={nestedMenuSx} />
                  <Collapse id="printify-navigation" in={printifyOpen} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      <NavItem to="/fulfillment/printify/stores" selected={selected.printifyStores} icon={<StoreIcon fontSize="small" />} label="Stores" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                      <NavItem to="/fulfillment/printify/catalog" selected={selected.printifyCatalog} icon={<CategoryIcon fontSize="small" />} label="Catalog" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                      <NavItem to="/fulfillment/printify/products" selected={selected.printifyProducts} icon={<Inventory2Icon fontSize="small" />} label="Products" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                      <NavItem to="/fulfillment/printify/orders" selected={selected.printifyOrders} icon={<ReceiptLongIcon fontSize="small" />} label="Orders" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                    </List>
                  </Collapse>

                  <NavToggle selected={etsySelected} open={etsyOpen} onClick={() => setEtsyOpen((current) => !current)} controls="etsy-navigation" icon={<PlatformIcon src="/assets/favicons/etsy.png" />} label="Etsy" sx={nestedMenuSx} />
                  <Collapse id="etsy-navigation" in={etsyOpen} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      <NavItem to="/sales/etsy/shops" selected={selected.etsyShops} icon={<StoreIcon fontSize="small" />} label="Shops" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                      <NavItem to="/sales/etsy/sections" selected={selected.etsySections} icon={<CategoryIcon fontSize="small" />} label="Sections" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                      <NavItem to="/sales/etsy/listings" selected={selected.etsyListings} icon={<ListAltIcon fontSize="small" />} label="Listings" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                    </List>
                  </Collapse>

                  <NavToggle selected={wooSelected} open={woocommerceOpen} onClick={() => setWooCommerceOpen((current) => !current)} controls="woocommerce-navigation" icon={<StoreIcon />} label="WooCommerce" sx={nestedMenuSx} />
                  <Collapse id="woocommerce-navigation" in={woocommerceOpen} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      <NavItem to="/sales/woocommerce/sites" selected={selected.wooSites} icon={<StoreIcon fontSize="small" />} label="Sites" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                      <NavItem to="/sales/woocommerce/categories" selected={selected.wooCategories} icon={<CategoryIcon fontSize="small" />} label="Categories" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                      <NavItem to="/sales/woocommerce/listings" selected={selected.wooListings} icon={<ListAltIcon fontSize="small" />} label="Listings" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                      <NavItem to="/sales/woocommerce/mockups" selected={selected.wooMockups} icon={<ImageIcon fontSize="small" />} label="Mockups" onNavigate={closeMobile} sx={{ pl: 6.5 }} />
                    </List>
                  </Collapse>
                  <NavItem to="/system/operations" selected={selected.operations} icon={<SyncIcon fontSize="small" />} label="Raw operations" onNavigate={closeMobile} sx={nestedMenuSx} />
                </>
              ) : null}
            </List>
          </Collapse>
        </NavSection>
      </Box>

      <Divider />
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <ListItemButton disabled sx={{ borderRadius: 2 }}>
          <ListItemIcon sx={menuIconSx}><PersonIcon /></ListItemIcon>
          <ListItemText primary={username || 'User'} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
        </ListItemButton>
        <Button
          fullWidth
          variant="text"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={() => Promise.resolve(logout()).catch((error) => console.error('Logout failed', error))}
          sx={{ mt: 0.5 }}
        >
          Log out
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box component="a" href="#main-content" className="skip-link">Skip to main content</Box>

      {!desktop ? (
        <AppBar position="fixed" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Toolbar>
            <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="Open primary navigation" sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
            <Box component="img" src="/assets/logo.webp" alt="POD Boss" sx={{ height: 34, width: 'auto' }} />
          </Toolbar>
        </AppBar>
      ) : null}

      <Drawer
        variant={desktop ? 'permanent' : 'temporary'}
        open={desktop || mobileOpen}
        onClose={closeMobile}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ component: 'nav', 'aria-label': 'Primary navigation' }}
        sx={{
          width: desktop ? drawerWidth : 0,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRightColor: 'divider',
            bgcolor: 'background.paper',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        id="main-content"
        tabIndex={-1}
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 1.5, sm: 2.5, xl: 4 },
          pt: { xs: 10, md: 2.5 },
          pb: `calc(${theme.spacing(2.5)} + ${jobsDockOffset}px)`,
          height: '100dvh',
          boxSizing: 'border-box',
          overflow: 'auto',
          background: 'linear-gradient(180deg, var(--md-sys-color-surface-container-low) 0%, transparent 240px)',
          '&:focus': { outline: 'none' },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 1920, mx: 'auto', minHeight: '100%' }}>{children}</Box>
      </Box>

      <JobsDock />
    </Box>
  );
};

export default SidebarLayout;
